import { TextField } from '~/common/components';
import { Trans, useI18n } from '~/common/context';
import { IOrder } from '@wyz/types';
import {
  Alert,
  Button,
  CenteredSpinner,
  ErrorMessage,
  FormContainer,
  Modal,
  notify,
  Text,
  Flex,
} from '@wyz/ui';
import { CANCELLED_ORDER_STATUSES } from '@wyz/utils';
import { useForm } from 'react-hook-form';
import { useManageRedistributionOrders } from '~/purchase/api';
import { usePurchaseErrorTranslation } from '~/common/hooks';
import { PROCESSED_ORDER_STATUS_LABEL_LOOKUP } from '~/purchase/translators';

type CancelProcessedOrderModalProps = {
  onClose: () => void;
  order: IOrder;
  customerId: string;
  tenantCode: string;
};

function CancelProcessedOrderModal({
  customerId,
  onClose,
  tenantCode,
  order,
}: CancelProcessedOrderModalProps) {
  const { t } = useI18n();
  const { resolveErrorKey } = usePurchaseErrorTranslation();
  const methods = useForm<{ comment?: string }>({
    defaultValues: {
      comment: '',
    },
  });

  const manageRedistributionOrders = useManageRedistributionOrders({
    cancel: { enabled: true },
    getGeneratedOrders: {
      enabled: true,
      args: { reference: order.orderReference, customerId, tenantCode },
    },
  });

  return (
    <Modal.Container open={true} onClose={onClose} size='lg' backdrop='static'>
      <Modal.Header>
        <Modal.Title>
          <Trans i18nKey='app.purchase.orders.processed.cancel_modal.title' />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Text heading='sm' weight='medium'>
          <Trans
            i18nKey='app.purchase.orders.processed.cancel_modal.description'
            values={{
              originalReference: order.orderReference,
              customerName: order.customerName,
              status: t(
                PROCESSED_ORDER_STATUS_LABEL_LOOKUP[order.logisticStatusId],
              ),
            }}
          />
        </Text>
        <FormContainer
          methods={methods}
          submitHandler={(form) => {
            manageRedistributionOrders.cancel.mutate(
              {
                tenantCode,
                customerId,
                orderReference: order.orderReference,
                comment: form.comment,
                status: CANCELLED_ORDER_STATUSES.CANCELLED_BY_SUPPLIER,
              },
              {
                onSuccess: (_, variables) => {
                  notify.error(
                    <Trans
                      i18nKey={
                        'app.purchase.orders.processed.cancel_modal.success_message'
                      }
                      values={{
                        orderReference: variables.orderReference,
                      }}
                      components={[<strong key={0} />]}
                    />,
                    { id: 'toast-error-processed-order-cancelled' },
                  );
                  onClose();
                },
                onError: (error) => {
                  notify.error(
                    <Trans i18nKey={resolveErrorKey(error.message)} />,
                    { id: 'toast-error-processed-order-cancel-failed' },
                  );
                },
              },
            );
          }}
          className='py-2'
        >
          {!manageRedistributionOrders.getGeneratedOrders.isSettled && (
            <CenteredSpinner />
          )}
          {manageRedistributionOrders.getGeneratedOrders.isError && (
            <ErrorMessage
              title={<Trans i18nKey='app.common.messages.error' />}
              message={
                manageRedistributionOrders.getGeneratedOrders.error?.message
              }
            />
          )}
          {manageRedistributionOrders.getGeneratedOrders.isSuccess && (
            <>
              <Alert variant='warning'>
                {manageRedistributionOrders.getGeneratedOrders.data.data
                  .length > 0
                  ? t(
                      'app.purchase.orders.processed.cancel_modal.generated_order_warning',
                      {
                        originalReference: order.orderReference,
                      },
                    )
                  : t(
                      'app.purchase.orders.processed.cancel_modal.no_generated_order_warning',
                      {
                        originalReference: order.orderReference,
                      },
                    )}
              </Alert>
              <TextField
                name='comment'
                type='text'
                as='textarea'
                label={<Trans i18nKey='app.common.comment_to_customer' />}
                placeholder={t('app.common.form.add_comment')}
                rows={6}
              />
              <Flex className='space-x-2'>
                <Button
                  id='cancel'
                  variant='secondary'
                  disabled={manageRedistributionOrders.cancel.isLoading}
                  onClick={onClose}
                  fullWidth
                >
                  <Trans i18nKey='app.common.button.cancel' />
                </Button>
                <Button
                  id='submit-cancel'
                  variant='danger'
                  type='submit'
                  isLoading={manageRedistributionOrders.cancel.isLoading}
                  fullWidth
                >
                  <Trans i18nKey='app.purchase.orders.processed.cancel_modal.cancel_button' />
                </Button>
              </Flex>
            </>
          )}
        </FormContainer>
      </Modal.Body>
    </Modal.Container>
  );
}

export default CancelProcessedOrderModal;
